export interface AgendaDay<T> {
  day: number;
  weekday: string;
  isToday: boolean;
  isPast?: boolean;
  items: T[];
}

export interface AgendaMonth<T> {
  month: number;
  year: number;
  label: string;
  isCurrent: boolean;
  days: AgendaDay<T>[];
}

export function isPastAgendaDay(year: number, month: number, day: number, today: Date): boolean {
  return year < today.getFullYear() || (year === today.getFullYear() &&
    (month < today.getMonth() || (month === today.getMonth() && day < today.getDate())));
}

export function createAgendaMonths<T>(today: Date): AgendaMonth<T>[] {
  return Array.from({ length: 24 }, (_, index) => {
    const offset = index - 12;
    const date = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return {
      month: date.getMonth(), year: date.getFullYear(),
      label: new Intl.DateTimeFormat('es-AR', { month: 'long' }).format(date),
      isCurrent: offset === 0, days: [],
    };
  });
}

export function groupAgendaItems<T>(items: T[], getDate: (item: T) => Date, today: Date): AgendaMonth<T>[] {
  const dated = items.map(item => ({ item, date: getDate(item) }))
    .filter(entry => !Number.isNaN(entry.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  return createAgendaMonths<T>(today).map(month => {
    const days = new Map<number, AgendaDay<T>>();
    for (const entry of dated) {
      const date = entry.date;
      if (date.getFullYear() !== month.year || date.getMonth() !== month.month) continue;
      const day = date.getDate();
      if (!days.has(day)) days.set(day, {
        day,
        weekday: new Intl.DateTimeFormat('es-AR', { weekday: 'short' }).format(date).replace('.', ''),
        isToday: month.isCurrent && day === today.getDate(),
        isPast: isPastAgendaDay(month.year, month.month, day, today), items: [],
      });
      days.get(day)!.items.push(entry.item);
    }
    return { ...month, days: [...days.values()] };
  }).filter(month => month.isCurrent || month.days.length > 0);
}
